import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FileUploader } from 'ng2-file-upload';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';

import { SpreadsheetParserWorksheet } from '../web-workers/spreadsheet-parser-worksheet';
import {
  AssignableField,
  AssignedField,
  GetAnalysisApiResponse,
  GetHeaderRowApiResponse,
  HeaderColumnsRow,
  UploadRequestsApiService,
  UploadTypesEnum,
} from '../upload-requests-api';
import { ChangeRequestApiService } from '../upload-requests-api/change-request-api.service';
import { FileLikeObject } from '@aptitude/angular-pipes';
import { UploadProductsModalParams } from './upload-products-modal-params';

import {
  ImportEntityFields,
  ModalConfigService,
} from './modal-config/modal-config.service';
import { getWorker } from './get-worker';
import { Features } from '../../../types/features';
import { User } from '../../../types/user/user';
import { RfpPermissions } from '../../../types/permissions/rfp-permissions';
import { Permissions } from '../../../types/permissions/permissions';
import { DownloadUtilService } from '../../../services/download-util.service';
import { NotyService } from '../../../services/noty.service';
import { CatalogGraphqlService } from '../../../api/graphql/catalog/catalog-graphql.service';

@Component({
    selector: 'app-upload-modal',
    templateUrl: './upload-modal.component.html',
    styleUrls: ['./upload-modal.component.scss'],
    standalone: false
})
export class UploadModalComponent implements OnInit, OnDestroy {
  activeTab = 'file';
  worksheets!: Array<SpreadsheetParserWorksheet>;
  selectedWorksheet!: SpreadsheetParserWorksheet | undefined;
  uploadType = '';
  modalTitle = '';
  public importEntityFields?: ImportEntityFields;
  executeChangeRequest$!: Observable<HttpResponse<void>>;
  navTabNames = ['file', 'tab', 'columns', 'summary'];
  uploadComplete = false;
  assignableFields!: Array<AssignableField> | undefined;
  columnHeadersData!: GetHeaderRowApiResponse;
  columnsTabData$?: Subscription;
  selectedHeaderRowIndex = 0;
  activeFileIndex = 0;
  headerColumnRows = [] as Array<HeaderColumnsRow>;
  fileUploader!: FileUploader;
  fileQueue: Array<FileLikeObject> = [];
  mappedColumnFields = new Map();
  isImportLoading = false;
  analysisSummaryData!: GetAnalysisApiResponse | undefined;
  isColumnsLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  isAnalysisLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  isSendingForReview: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  analysisComplete = false;
  importComplete = false;
  allowedMimeTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ];
  downloading = false;
  featureFlags?: Features;
  currentUser!: User;
  resourceId!: number;

  permissions?: Permissions;
  rfpPermissionsGraphql?: RfpPermissions;

  isSendForReviewAllowed = false;
  sentForReviewCount?: number;
  baseApiUrl?: string;
  theme?: 'admin';

  @Input() modalParams$: BehaviorSubject<UploadProductsModalParams> =
    new BehaviorSubject({} as UploadProductsModalParams);

  constructor(
    public activeModal: NgbActiveModal,
    private uploadRequestApi: UploadRequestsApiService,
    private changeRequestApi: ChangeRequestApiService,
    private downloadService: DownloadUtilService,
    private notify: NotyService,
    private modalConfigService: ModalConfigService,
    private catalogGraphqlService: CatalogGraphqlService,
  ) {}

  ngOnInit(): void {
    this.modalParams$.subscribe((modalParams: UploadProductsModalParams) => {
      this.uploadType = !!modalParams.uploadType ? modalParams.uploadType : '';
      this.modalTitle = modalParams.modalTitle;
      this.resourceId = modalParams.resourceId;
      this.featureFlags = modalParams.featureFlags;
      this.currentUser = modalParams.currentUser;
      this.permissions = modalParams.permissions;
      this.rfpPermissionsGraphql = modalParams.rfpPermissionsGraphql;
      this.baseApiUrl = modalParams.baseApiUrl ?? '/aptitude';
      this.theme = modalParams.theme;
      this.importEntityFields =
        this.modalConfigService.getUploadResourceDataParams(
          modalParams.importEntity,
          { resourceId: modalParams.resourceId },
        );

      const hasSendForReviewPermission =
        this.permissions?.IMPORT?.SEND_FOR_REVIEW?.hasAccess ||
        this.rfpPermissionsGraphql?.SEND_FOR_REVIEW?.hasAccess;

      this.isSendForReviewAllowed = !!hasSendForReviewPermission;
    });
  }

  submitForReview() {
    const fileId = this.activeFile?.id as string;
    // Set to -1 so we know submitForReview has been clicked. Fixme: use observable.
    this.sentForReviewCount = -1;
    this.uploadRequestApi.submitReviewRequest(fileId).subscribe(
      (val) => {
        this.sentForReviewCount = val.sentForReviewCount || 0;
      },
      (error) => {
        this.sentForReviewCount = undefined;
        this.notify.error(error.error.message);
      },
    );
  }

  onFileSelected(files: File[]) {
    const { name, size, type } = files[0];
    let error = '';

    if (!this.allowedMimeTypes.find((allowedType) => allowedType === type)) {
      error = 'Invalid file format';
    }

    const activeFile = {
      name,
      size,
      type,
      error,
      rawFile: files[0],
      isLoading: !error,
    } as FileLikeObject;

    this.fileQueue = [activeFile];

    if (!error && this.resourceId != null) {
      const { uploadType, resourceType, resourcePath } = this
        .importEntityFields as ImportEntityFields;

      this.uploadRequestApi
        .uploadFiles(files, {
          uploadType,
          resourceType,
          resourcePath,
          resourceId: this.resourceId, // This will be the category Id. Value is not used by backend yet.
        })
        .subscribe(
          (fileInfo) => {
            activeFile.isLoading = false;
            activeFile.id = fileInfo[0].id.toString();
            this.setFileWorksheets(activeFile.rawFile);
          },
          (responseError) => {
            activeFile.error = responseError.message;
          },
        );
    }
  }

  getFileByName(fileName: string): FileLikeObject | undefined {
    return this.fileQueue.find((file) => {
      return (file.name = fileName);
    });
  }

  setFileWorksheets(file: File) {
    console.log('uploadType', this.uploadType);
    const worker = getWorker();

    const activeFile = this.getFileByName(file.name);

    this.selectedWorksheet = undefined;
    this.headerColumnRows = [];
    this.assignableFields = undefined;
    this.analysisSummaryData = undefined;
    this.sentForReviewCount = undefined;

    worker.onmessage = (workerResponse) => {
      this.worksheets = JSON.parse(workerResponse.data);

      if (this.worksheets[0]) {
        this.updateSelectedWorksheet(this.worksheets[0]);
      } else if (activeFile) {
        activeFile.error = 'No content found.';
      }
    };

    worker.postMessage(file);
  }

  updateSelectedWorksheet(ws: SpreadsheetParserWorksheet) {
    this.selectedWorksheet = ws;
    const fileId = this.activeFile?.id;

    if (fileId) {
      this.isColumnsLoading.next(true);
      this.columnsTabData$ = combineLatest([
        this.importEntityFields?.uploadType === UploadTypesEnum.CROSSREFERENCE && this.featureFlags?.CONSUME_PRODUCT_DATA_MD_CAT_964488 ?
          this.catalogGraphqlService.getAssignableFields() :
          this.uploadRequestApi.getAssignableFields(fileId),
        this.uploadRequestApi.getHeaderRow(
          fileId,
          this.selectedWorksheet.rowsData,
        ),
      ]).subscribe(([assignableFields, columnsHeadersData]) => {
        this.headerColumnRows = columnsHeadersData.rows;
        this.assignableFields = assignableFields;
        this.updateSelectedHeaderRowIndex(columnsHeadersData.headerIndex);
        this.isColumnsLoading.next(false);
      });
    }
  }

  get activeTabNavIndex(): number {
    return this.navTabNames.indexOf(this.activeTab);
  }

  nextTab() {
    const index = this.activeTabNavIndex;
    this.activeTab = this.navTabNames[index + 1] || this.navTabNames[index];
  }

  previousTab() {
    const index = this.activeTabNavIndex;
    this.activeTab = this.navTabNames[index - 1] || this.navTabNames[index];
  }

  closeModal() {
    this.activeModal.close();
  }

  dismissModal() {
    this.activeModal.dismiss();
  }

  canShowButton(button: string): boolean {
    // TODO: CQVA-501 we need to clean up this logic.
    if (
      button === 'next' &&
      (this.activeTabNavIndex === 0 ||
        this.activeTabNavIndex === 1 ||
        this.activeTabNavIndex === 2)
    ) {
      return true;
    } else if (
      button === 'previous' &&
      (this.activeTabNavIndex === 1 ||
        this.activeTabNavIndex === 2 ||
        this.activeTabNavIndex === 3)
    ) {
      return true;
    } else if (button === 'import' && this.activeTabNavIndex === 3) {
      return true;
    } else if (button === 'view-products' && this.activeTab === '') {
      return true;
    } else if (button === 'view-rejects' && this.activeTab === '') {
      return true;
    } else if (button === 'send-for-review' && this.activeTab === '') {
      return true;
    }
    return false;
  }

  import() {
    this.isImportLoading = true;
    if (this.fileQueue[0].id) {
      this.changeRequestApi.updateChangeRequest(this.fileQueue[0].id).subscribe(
        (x) => {
          if (x.status === 204) {
            this.importComplete = true;
            this.isImportLoading = false;
            this.activeTab = '';
          }
        },
        (error: HttpErrorResponse) => {
          this.notify.error(error.error.message);
          this.isImportLoading = false;
          this.activeTab = '';
        },
      );
    }
  }

  canImport(): boolean {
    if (
      this.analysisSummaryData &&
      !this.isAnalysisLoading.getValue() &&
      !this.isImportLoading
    ) {
      return (
        this.analysisSummaryData.recordMatchedCount > 0 ||
        this.analysisSummaryData.recordDeletionCount > 0
      );
    }
    return false;
  }

  hasViolation(): boolean {
    if (this.analysisSummaryData) {
      return this.analysisSummaryData.recordViolationCount > 0;
    }
    return false;
  }

  ngOnDestroy() {
    this.columnsTabData$?.unsubscribe();
    this.modalParams$.unsubscribe();
  }

  updateMappedColumnFields(columnFieldsMap: Map<string, string>) {
    this.mappedColumnFields = columnFieldsMap;
    this.analysisSummaryData = undefined;
    this.sentForReviewCount = undefined;
  }

  updateSelectedHeaderRowIndex(rowIndex: number) {
    this.selectedHeaderRowIndex = rowIndex;

    this.matchColumnFieldsToAssignableFields(
      this.headerColumnRows[this.selectedHeaderRowIndex],
      this.assignableFields || [],
    );
  }

  // We need to call this every time the selectedRowIndex changes to preset assigned columns and fields.
  matchColumnFieldsToAssignableFields(
    headerColumnsRow: HeaderColumnsRow,
    assignableFields: Array<AssignableField>,
  ) {
    this.mappedColumnFields.clear();
    const newMappedFields = new Map();

    headerColumnsRow.headerColumns.forEach((headerColumn) => {
      if (headerColumn.mapFieldName) {
        const assignableField = assignableFields.find(
          (field) => field.name === headerColumn.mapFieldName,
        );

        if (assignableField) {
          newMappedFields.set(assignableField.name, headerColumn.columnHeader);
        }
      }
    });

    this.updateMappedColumnFields(newMappedFields);
  }

  hasMappingForRequiredFields(): boolean {
    const hasRequiredFields = this.assignableFields?.every((field) => {
      let isValid = false;

      isValid = field.required
        ? !!this.mappedColumnFields.get(field.name)
        : true;

      return isValid;
    });

    return hasRequiredFields || false;
  }

  getImportCrossesSummary() {
    if (
      this.hasMappingForRequiredFields() &&
      this.fileQueue[0].id &&
      !this.analysisSummaryData &&
      this.selectedWorksheet &&
      this.assignableFields
    ) {
      const fields = [] as Array<AssignedField>;
      this.assignableFields.forEach((field) => {
        fields.push({
          columnHeader: this.mappedColumnFields.get(field.name) || '',
          mapFieldName: field.name,
        });
      });

      this.isAnalysisLoading.next(true);

      this.uploadRequestApi
        .getAnalysis(
          this.fileQueue[0].id,
          this.selectedHeaderRowIndex,
          this.selectedWorksheet.name,
          fields,
        )
        .subscribe({
          next: (data) => {
            this.analysisSummaryData = data;
            this.analysisComplete = true;
            this.isAnalysisLoading.next(false);
          },
          error: (error: HttpErrorResponse) => {
            this.notify.error(error.error.message);
            this.analysisComplete = true;
            this.isAnalysisLoading.next(false);
          },
        });
    } else {
      this.isAnalysisLoading.next(false);
    }
  }

  hasCrossesSummaryData(): boolean {
    return !!(
      this.fileQueue[0]?.id &&
      this.hasMappingForRequiredFields() &&
      this.selectedWorksheet &&
      this.analysisSummaryData
    );
  }

  download() {
    if (this.analysisSummaryData) {
      this.downloading = true;
      this.downloadService
        .download(
          `${this.baseApiUrl}/api${this.analysisSummaryData.downloadUrl}`,
          'download.xlsx',
        )
        .subscribe(() => {
          this.downloading = false;
        });
    }
  }

  getSelectedFileId(): string | undefined {
    return this.fileQueue[0]?.id;
  }

  get activeFile(): FileLikeObject | undefined {
    return this.fileQueue[this.activeFileIndex];
  }
}
